import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import qs from 'qs'
import { FormattedMessage } from 'react-intl'
import { omit, omitBy, pick } from 'lodash'
import { useMediaQuery } from 'react-responsive'
// Components
import { Button, Form, Text } from 'src/Components'
import DollarInput from 'src/Components/shared/Input/DollarInput'
import RoomCount from 'src/Components/homeSearch/Filters/RoomCount'
import Price from 'src/Components/homeSearch/Filters/Price'
import BuildingSize from 'src/Components/homeSearch/Filters/BuildingSize'
import ConstructionYear from 'src/Components/homeSearch/Filters/ConstructionYear'
import HomeType from 'src/Components/homeSearch/Filters/HomeType'
// Selectors
import { selectFilter } from 'src/store/selectors/filters'
// Utils
import { constructFiltersAndOmitEmpty } from 'src/helpers/listings/filters'
import history from 'src/history'
import { camelizeKeys, snakeCaseKeys } from 'src/helpers/utils'
// Constants
import {
  HOME_TYPES_APTCONDO,
  HOME_TYPES_HOUSE,
  HOME_TYPES_LOTLAND,
  HOME_TYPES_TOWNHOUSE,
  MAX_PRICE_CENTS,
  MAX_PRICE_DOLLARS,
  MAX_SQFT,
  MAX_YEAR_BUILT,
  MIN_BATHROOMS_COUNT,
  MIN_BEDROOMS_COUNT,
  MIN_PRICE_CENTS,
  MIN_PRICE_DOLLARS,
  MIN_SQFT,
  MIN_YEAR_BUILT,
  PROPERTY_TYPES,
} from 'src/constants/listings'
// Styles
import styles from './Filters.module.scss'
import { ListViewFilterViewSpacingWrapper } from '../ListViewFilterViewSpacingWrapper/ListViewFilterViewSpacingWrapper'

const Filters = ({ close }) => {
  const filters = useSelector(selectFilter)

  const isMediumScreen = useMediaQuery({
    query: `(min-width: ${styles.mediumBreak})`,
  })

  const isMediumMaxScreen = useMediaQuery({
    query: `(max-width: ${styles.mediumBreak})`,
  })

  const handleSubmit = (e, data) => {
    e.preventDefault()

    const queryParams = camelizeKeys(
      qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      })
    )
    data[MIN_BATHROOMS_COUNT] = parseInt(data[MIN_BATHROOMS_COUNT], 10)
    data[MIN_BEDROOMS_COUNT] = parseInt(data[MIN_BEDROOMS_COUNT], 10)
    data[PROPERTY_TYPES] = Object.keys(
      omitBy(
        pick(data, [
          HOME_TYPES_HOUSE,
          HOME_TYPES_APTCONDO,
          HOME_TYPES_TOWNHOUSE,
          HOME_TYPES_LOTLAND,
        ]),
        value => !value
      )
    )
    const searchFilters = constructFiltersAndOmitEmpty(data)
    const newQueryParams = {
      ...queryParams,
      ...searchFilters,
    }

    const path = `${window.location.pathname}?${snakeCaseKeys(
      qs.stringify(newQueryParams)
    )}`

    history.push(path)

    close()
  }

  const resetFilters = e => {
    e.preventDefault()

    const queryParams = camelizeKeys(
      qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      })
    )

    const newQueryParams = omit({ ...queryParams }, [
      MIN_PRICE_CENTS,
      MAX_PRICE_CENTS,
      MIN_BATHROOMS_COUNT,
      MIN_BEDROOMS_COUNT,
      MIN_SQFT,
      MAX_SQFT,
      MIN_YEAR_BUILT,
      MAX_YEAR_BUILT,
      PROPERTY_TYPES,
    ])

    const path = `${window.location.pathname}?${snakeCaseKeys(
      qs.stringify(newQueryParams)
    )}`

    history.push(path)
    close()
  }

  const bathsHeading = (
    <FormattedMessage
      id='HomeSearch.listings.filters.baths.heading'
      defaultMessage='Baths'
    />
  )

  const bedsHeading = (
    <FormattedMessage
      id='HomeSearch.listings.filters.beds.heading'
      defaultMessage='Beds'
    />
  )

  return (
    <Form
      fields={[
        { name: MIN_PRICE_DOLLARS, parse: DollarInput.parseValue },
        { name: MAX_PRICE_DOLLARS, parse: DollarInput.parseValue },
        MIN_BATHROOMS_COUNT,
        MIN_BEDROOMS_COUNT,
        MIN_YEAR_BUILT,
        MAX_YEAR_BUILT,
        { name: MIN_SQFT, parse: BuildingSize.parseValue },
        { name: MAX_SQFT, parse: BuildingSize.parseValue },
        HOME_TYPES_HOUSE,
        HOME_TYPES_APTCONDO,
        HOME_TYPES_TOWNHOUSE,
        HOME_TYPES_LOTLAND,
      ]}
      onSubmit={handleSubmit}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          {isMediumMaxScreen && (
            <div>
              <Button
                className={styles.cancelButtonMobile}
                onClick={e => {
                  e.preventDefault()
                  close()
                }}
              >
                <FormattedMessage
                  id='HomeSearch.listings.filters.cancel'
                  defaultMessage='Cancel'
                />
              </Button>
            </div>
          )}
          <div>
            <Text
              className={styles.heading}
              align='left'
              element='h1'
              size='xl'
              weight='bold'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='HomeSearch.listings.filters.heading'
                defaultMessage='Filter Results'
              />
            </Text>
          </div>
          {isMediumMaxScreen && (
            <div>
              <Button type='submit' className={styles.saveButton}>
                <FormattedMessage
                  id='HomeSearch.listings.filters.save'
                  defaultMessage='Save'
                />
              </Button>
            </div>
          )}
        </div>
        <div className={styles.section}>
          <Price />
        </div>
        <div className={styles.section}>
          <RoomCount
            heading={bedsHeading}
            name={MIN_BEDROOMS_COUNT}
            value={filters[MIN_BEDROOMS_COUNT]}
          />
        </div>
        <div className={styles.section}>
          <RoomCount
            heading={bathsHeading}
            name={MIN_BATHROOMS_COUNT}
            value={filters[MIN_BATHROOMS_COUNT]}
          />
        </div>
        <div className={styles.section}>
          <HomeType />
        </div>
        <div className={styles.section}>
          <BuildingSize />
        </div>
        <div className={styles.section}>
          <ConstructionYear />
        </div>
      </div>
      <div className={styles.buttons}>
        <div className={styles.mediumScreenButtons}>
          <Button className={styles.resetButton} onClick={resetFilters}>
            <FormattedMessage
              id='HomeSearch.listings.filters.reset'
              defaultMessage='Reset all filters'
            />
          </Button>
        </div>

        {isMediumScreen && (
          <div className={styles.mediumScreenButtons}>
            <Button
              className={styles.cancelButton}
              onClick={e => {
                e.preventDefault()
                close()
              }}
            >
              <FormattedMessage
                id='HomeSearch.listings.filters.cancel'
                defaultMessage='Cancel'
              />
            </Button>
            <Button primary type='submit' data-qa='applyFilter'>
              <FormattedMessage
                id='HomeSearch.listings.filters.apply'
                defaultMessage='Apply'
              />
            </Button>
          </div>
        )}
      </div>
    </Form>
  )
}

Filters.propTypes = {
  close: PropTypes.func.isRequired,
}

export default Filters
